/** `W-40d` — **못 만든 장을 결판내는 자리**. 앱 `A-52d` 시트와 **같은 물음 · 같은 글자**이고
 * 모양만 다르다. 폰은 바닥 시트, 웹은 가운데 모달이다(PRD `7.7.5a`).
 *
 * ## ⛔ 여기서 문장을 다시 고르지 않는다
 *
 * 제목 · 본문 세 문장 · 그룹 · 버튼 둘 · 사유 줄이 전부 `@makit/shared` 의
 * `exportConfirm*` 에서 온다. 앱 `features/job/export-decline-sheet.tsx` 가 **같은 함수**를
 * 부른다. ⛔ 이 파일에 한글 문장을 박지 마라.
 *
 * ## ⛔ `ConfirmDialog` 를 쓰지 않는다
 *
 * 그쪽은 주 버튼이 `danger` 인데 **여기서 셀러가 잃는 것이 없다** — 파일은 그대로 나가고 돈은
 * 돌아온다. 그리고 그 확인창에는 **결판날 장 전부를 세울 그룹 자리가 없다**(`7.6.14b` ⓒ 2번).
 * `features/upload/PrecutModal.tsx` 와 같은 틀(`.scrim` · `.modal`)을 쓴다.
 *
 * ## ⭐⭐ 닫는 길 전부가 `CLOSE` 하나로 모인다
 *
 * 아래 `닫기` · 모달 밖 누르기 · `Esc` 셋이 같은 값이고, 그 값이 무엇을 하는지는
 * `exportConfirmOutcome` 이 정한다(**아무 일도 안 일어난다**). ⛔ 셋 중 하나라도 다른 쪽으로
 * 이으면 **안 읽고 닫은 것이 돈이 걸린 선택**이 된다.
 *
 * ## ⭐ 요청이 나가 있는 동안(`pending`)은 세 닫는 길을 전부 막는다
 *
 * 그때는 `만들지 않기` 가 이미 서버로 가고 있다. 닫아도 결판과 내보내기는 계속 나가므로
 * 사유 줄의 「닫으면 아무것도 나가지 않고」가 거짓이 된다. ⛔ 셋 중 하나만 막지 마라.
 * 막히지 않은 길 하나가 같은 거짓을 연다. */

import { useEffect, useRef } from "react";
import {
  exportConfirmActions,
  exportConfirmBlock,
  exportConfirmBody,
  exportConfirmPickHint,
  exportConfirmTitle,
  type ExportConfirmPress,
  type ExportDeclineView,
} from "@makit/shared";

import { Button } from "../../components/primitives";
import { DeclineBlock } from "./DeclineBlock";

export interface ExportDeclineModalProps {
  view: ExportDeclineView;
  /** 셀러가 누른 것. **무엇을 할지는 부른 쪽이 `exportConfirmOutcome` 으로 가른다.** */
  onPress: (press: ExportConfirmPress) => void;
  /** 결판을 적고 내보내는 요청이 나가 있다. 두 번 누르면 결판을 두 번 적는다. */
  pending: boolean;
}

export function ExportDeclineModal({ view, onPress, pending }: ExportDeclineModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const actions = exportConfirmActions();
  const title = exportConfirmTitle(view);

  useEffect(() => {
    opener.current = document.activeElement;
    // ⭐ **첫 포커스는 `닫기`(마크업상 첫 버튼)다.** 채운 버튼이 눈으로 읽는 기본값인 것과
    //    다투지 않는다. 엔터를 치는 사람이 읽지 않은 채 돈이 걸린 쪽으로 가지 않게 하는 줄이다
    //    (`7.7.5a`. `PrecutModal` 과 같은 근거).
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => {
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      // ⛔ **`Esc` 는 `닫기` 와 같은 일이다.**
      //    요청이 나가 있으면 아무 일도 하지 않는다(머리말).
      if (event.key === "Escape" && !pending) onPress("CLOSE");
      if (event.key !== "Tab" || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>("button:not([disabled]), [href]");
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onPress, pending]);

  return (
    <div
      className="scrim"
      // ⛔ **모달 밖 누르기도 `닫기` 다.** 위 `Esc` 와 같은 값을 쓴다.
      onMouseDown={(e) => !pending && e.target === e.currentTarget && onPress("CLOSE")}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <span className="st">{title}</span>
        <span className="sb prose">{exportConfirmBody()}</span>
        {/* ⚠️ **수만 적은 요약으로 줄이지 않는다.** 누르기 직전 화면에 결판날 장이 전부 있어야
            한다(`7.6.14b` ⓒ 2번). 모달에는 링크가 없다 — shared 가 `link: null` 을 준다. */}
        <DeclineBlock block={exportConfirmBlock(view)} />
        {/* 마크업 차례가 **좌 ghost / 우 primary** 다. `.row` 가 `wrap-reverse` 라 쌓이면
            주 동작이 위로 온다(`7.5.1`). ⛔ 주 버튼에 `danger` 를 쓰지 않는다. */}
        <div className="row">
          <Button
            label={actions.secondary.label}
            variant="ghost"
            disabled={pending}
            onClick={() => onPress("CLOSE")}
          />
          <Button
            label={actions.primary.label}
            disabled={actions.primary.disabled || pending}
            onClick={() => onPress("DECLINE")}
          />
        </div>
        <p className="pickhint prose">{exportConfirmPickHint()}</p>
      </div>
    </div>
  );
}
